"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"

import { findImprovementsQueryKey, qualitySuggestionsQueryKey } from "@/hooks/queries/conversation-quality"
import { agentSelfLearningQueryKey } from "@/hooks/queries/self-learning"
import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"

import {
  setSelfLearningArticlesAction,
  setSelfLearningModeAction,
  type SelfLearningView,
  type SetSelfLearningModeInput,
} from "@/server-actions/self-learning"
import { useTranslations } from "next-intl"


type SetErrors = (errors: Record<string, unknown>) => void

/** Off closes the agent's waiting drafts and stops its presses, so both lists are read again. */
export function useSetSelfLearningModeMutation(agentId: string) {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsSelfLearning")

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      input,
      setErrors,
    }: {
      input: Omit<SetSelfLearningModeInput, "agentId">;
      setErrors?: SetErrors;
    }) => {
      const result = await withQueryHandling(setSelfLearningModeAction({ agentId, ...input }), {
        loading: tUi("savingSelfLearning"),
        success: tUi("selfLearningSaved"),
        setErrors,
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: (state) => {
      queryClient.setQueryData<SelfLearningView>(
        agentSelfLearningQueryKey(agentId),
        (view) => view && { ...view, ...state }
      )
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: agentSelfLearningQueryKey(agentId) }),
        queryClient.invalidateQueries({ queryKey: qualitySuggestionsQueryKey }),
        queryClient.invalidateQueries({ queryKey: findImprovementsQueryKey }),
      ]),
  })
}

export function useSetSelfLearningArticlesMutation(agentId: string) {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsSelfLearning")

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (addsArticles: boolean) => {
      const result = await withQueryHandling(setSelfLearningArticlesAction({ agentId, addsArticles }), {
        loading: tUi("savingSelfLearning"),
        success: tUi("selfLearningSaved"),
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: (state) => {
      queryClient.setQueryData<SelfLearningView>(
        agentSelfLearningQueryKey(agentId),
        (view) => view && { ...view, ...state }
      )
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: agentSelfLearningQueryKey(agentId) }),
  })
}
